import React from "react";
import { useTranslation } from "react-i18next";
import { PendingBackendNotice } from "../../../../../components/ui/PendingBackendNotice";
import { Switch } from "../../../../../components/ui/Switch";
import {
  disabledScopeLabels,
  followChannel,
  formatScopePhone,
} from "../aiScope";
import { SectionTitle } from "./primitives";

// Dónde responde ESTE agente: canal por canal y cuenta por cuenta.
//
// Modelo mental (el mismo de aiScope.js): cada número sigue a su canal salvo
// que el admin lo deje distinto — y solo esas diferencias viajan al backend.
// Las claves de canal y de cuenta las arma el backend; acá no se inventa
// ninguna.

// Estado de una cuenta cuando NO está operativa (el 'active' no se rotula: es
// lo normal). Se muestra porque apagar un agente en un número caído es
// información distinta a apagarlo en uno que sí recibe.
const ACCOUNT_STATUS_LABELS = {
  pending: "sin conectar",
  inactive: "desconectado",
  error: "con error",
};

export default function ScopeSection({ value, audience, onChange }) {
  const { t } = useTranslation();
  const channels = Array.isArray(value?.channels) ? value.channels : [];
  const channelStates = value?.channelStates || {};
  const accountStates = value?.accountStates || {};

  // Audiencias sin canal propio: el hint explica por qué el agente no
  // responde en ninguna parte hasta que el admin encienda lo que corresponda.
  const explicitOnly = audience === "driver" || audience === "internal";
  const offLabels = disabledScopeLabels(channels, channelStates, accountStates);
  // El hint de arriba afirma que este agente solo responde donde se lo encienda
  // a propósito. Si igual quedó algo encendido (p. ej. al cambiarle la audiencia
  // a un agente que ya existía), se dice: el estado manda sobre el texto.
  const anyOn =
    channels.some((c) => channelStates[c.channel] !== false) ||
    Object.values(accountStates).some((on) => on !== false);

  const toggleChannel = (channel) => {
    const current = channelStates[channel] !== false;
    const next = !current;
    const accounts = channels.find((c) => c.channel === channel)?.accounts || [];
    onChange({
      ...value,
      channelStates: { ...channelStates, [channel]: next },
      // Apagar un canal arrastra a las cuentas que iban en línea con él y
      // respeta las excepciones ya definidas.
      accountStates: followChannel(accounts, accountStates, current, next),
    });
  };

  const toggleAccount = (key) => {
    onChange({
      ...value,
      accountStates: { ...accountStates, [key]: accountStates[key] === false },
    });
  };

  return (
    <section className="space-y-3">
      <SectionTitle>{t("Dónde responde")}</SectionTitle>
      <p id="agent-scope-help" className="text-xs text-fg-muted">
        {t("Elige en qué canales y números atiende este agente. Un número puede llevarle la contraria a su canal.")}
      </p>

      {explicitOnly && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Este agente solo responde donde lo enciendas explícitamente.")}
        </p>
      )}
      {explicitOnly && anyOn && (
        <p className="text-[12px] md:text-[11px] text-status-warning" role="status">
          {t("Tiene canales encendidos. Revisa que sean los que corresponden a esta audiencia.")}
        </p>
      )}

      {channels.length === 0 ? (
        <PendingBackendNotice>
          {t("No pudimos cargar tus canales; el agente conservará los que ya tenía.")}
        </PendingBackendNotice>
      ) : (
        <div className="divide-y divide-border">
          {channels.map((c) => {
            const channelOn = channelStates[c.channel] !== false;
            const accounts = c.accounts || [];
            return (
              <div key={c.channel} className="py-3 first:pt-0 last:pb-0 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-fg truncate">{c.label}</span>
                    {accounts.length > 0 && (
                      <span className="block text-[12px] md:text-[11px] text-fg-muted">
                        {accounts.length === 1
                          ? t("1 cuenta conectada")
                          : t("{{n}} cuentas conectadas", { n: accounts.length })}
                      </span>
                    )}
                  </span>
                  <Switch
                    size="sm"
                    checked={channelOn}
                    onChange={() => toggleChannel(c.channel)}
                    ariaLabel={t("Agente en {{canal}}", { canal: c.label })}
                    aria-describedby="agent-scope-help"
                  />
                </div>

                {accounts.length > 0 && (
                  <ul className="space-y-2.5 pl-3 border-l border-border">
                    {accounts.map((a) => {
                      const on = accountStates[a.key] !== false;
                      // Difiere de su canal => es una excepción explícita, y
                      // así viaja en el guardado.
                      const isException = on !== channelOn;
                      return (
                        <li key={a.key} className="flex items-center justify-between gap-3">
                          <span className="min-w-0">
                            <span className="block text-xs text-fg truncate">{a.label}</span>
                            <span className="flex items-center gap-1.5 min-w-0 text-[12px] md:text-[11px] text-fg-muted">
                              <span className="truncate">
                                {a.phone ? formatScopePhone(a.phone) : t("sin número asociado")}
                              </span>
                              {ACCOUNT_STATUS_LABELS[a.status] && (
                                <span className="shrink-0">
                                  · {t(ACCOUNT_STATUS_LABELS[a.status])}
                                </span>
                              )}
                              {isException && (
                                <span className="shrink-0 inline-flex items-center rounded border border-border bg-bg-muted px-1.5 py-0.5 text-[11px] md:text-[10px] font-medium text-fg-muted">
                                  {t("excepción")}
                                </span>
                              )}
                            </span>
                          </span>
                          <Switch
                            size="sm"
                            checked={on}
                            onChange={() => toggleAccount(a.key)}
                            ariaLabel={t("Agente en {{cuenta}}", { cuenta: a.label })}
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {offLabels.length > 0 && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("No responde en: {{lista}}.", { lista: offLabels.join(", ") })}
        </p>
      )}
    </section>
  );
}
